import { forwardRef, ImgHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

// hooks
import useImageLoaded from '@/hooks/useImageLoaded';
import Typography from '@/components/elements/Typography';

// icons
import PersonIcon from '@/components/modules/icons/Person';

type Size = 'small' | 'medium' | 'large';

export type AvatarProps = {
  size?: Size;
} & ImgHTMLAttributes<HTMLImageElement>;

// button variants
const SIZE_MAPS: Record<Size, string> = {
  small: `w-8 h-8`,
  medium: `w-12 h-12`,
  large: `w-16 h-16`,
};

/**
 * @component Avatar
 */
const Avatar = forwardRef<HTMLDivElement, AvatarProps>((props, ref) => {
  const {
    size = `medium`,
    className,
    src,
    srcSet,
    alt,
    children: childrenProp,
    ...rest
  } = props;

  const { state } = useImageLoaded({ src, srcSet });

  const hasImg = src || srcSet;
  const hasImgNotFailing = hasImg && state !== `error`;

  const rootClasses = clsx(
    `bg-orange-500 rounded-full overflow-hidden text-gray-50 flex relative items-center flex-shrink-0 select-none justify-center`,
    SIZE_MAPS[size],
    className,
  );
  const imageClasses = clsx(`w-full h-full object-cover object-center`);

  let children: ReactNode = null;

  const renderImage = () => (
    <img alt={alt} src={src} srcSet={srcSet} className={imageClasses} />
  );
  if (hasImgNotFailing) {
    children = renderImage();
  } else if (childrenProp != null) {
    children = childrenProp;
  } else if (hasImg && alt) {
    // eslint-disable-next-line prefer-destructuring
    children = alt[0];
  } else {
    children = <PersonIcon className="!text-gray-50" />;
  }

  return (
    <Typography
      ref={ref}
      as="div"
      variant="paragraph"
      className={rootClasses}
      {...rest}
    >
      {children}
    </Typography>
  );
});

export default Avatar;
